import { FC, useCallback, useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button, Form, Input, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { Loader } from '@/components';
import {
  CalculatorSettings,
  KPISummary,
  RatesTable,
  AddRateModal,
  EmptyState
} from '@/pages/EU6Calculator/components';
import { useEU6Calculator } from '@/pages/EU6Calculator/hooks/useEU6Calculator';
import type {
  RateCard,
  RateCardInput,
  Currency,
  CalculatorSettings as CalculatorSettingsState
} from '@/pages/EU6Calculator/EU6Calculator.types';
import {
  useCreateCalculatorPackageMutation,
  useUpdateCalculatorPackageMutation,
  useGetCalculatorPackageByIdQuery
} from '@/api/calculatorPackages';
import { buildCalculatorPackageWriteBody } from '@/api/calculatorPackageMappers';
import { packageBudgetPersistedMismatch } from '@/api/calculatorPackagePersistedCheck';
import { useGetTradingDealsQuery } from '@/api';
import type {
  CalculatorPackage,
  CalculatorPackageRow,
  CreateCalculatorPackageBody
} from '@/api/calculatorPackages';
import { NAMESPACE } from '@/constants';
import { PageContainer, TableCard, SectionHeading } from '@/pages/EU6Calculator/EU6Calculator.styles';
import { isTotalDeliveryPercentageOver100 } from '@/pages/EU6Calculator/sumRateCardsDeliveryPercentage';
import {
  calculatorSettingsFromPackage,
  fxRatesFromPackage,
  tradingDealSettingsFromPackage
} from '@/pages/EU6Calculator/calculatorSettingsFromPackage';
import { buildDuplicateInitialPackage } from '../buildDuplicateInitialPackage';
import { APP_SHELL_THEME } from '@/styles/appShellTheme';

function rateCardToPackageRow(card: RateCard): CalculatorPackageRow {
  return {
    country: card.country,
    channel: card.channel,
    publisher: card.publisher,
    format: card.format,
    deliveryPercentage: card.deliveryPercentage,
    rowCurrency: card.rowCurrency,
    buyCPM: card.buyCPM,
    dspFeePercentage: card.dspFeePercentage,
    dspName: card.dspFeeName || undefined
  };
}

function packageRowToRateCardInput(row: CalculatorPackageRow): RateCardInput {
  return {
    country: row.country,
    channel: row.channel,
    publisher: row.publisher,
    format: row.format,
    deliveryPercentage: row.deliveryPercentage,
    rowCurrency: row.rowCurrency as Currency,
    buyCPM: row.buyCPM,
    dspFeeId: null,
    dspFeeName: row.dspName ?? '',
    dspFeePercentage: row.dspFeePercentage
  };
}

export interface CalculatorPackageModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after save; receives the created/updated package from the API when available. */
  onSuccess: (savedPackage?: CalculatorPackage) => void;
  /** When editing, the package id. When creating, null. */
  packageId: string | null;
  /** When editing, the existing package (for prefill). When creating, null. */
  initialPackage: CalculatorPackage | null;
  /** True when creating a new package prefilled from an existing one (e.g. list duplicate). */
  isDuplicating?: boolean;
  /** Source package id when duplicating — used to load full package settings (incl. budget). */
  duplicateSourcePackageId?: string;
}

export const CalculatorPackageModal: FC<CalculatorPackageModalProps> = ({
  open,
  onClose,
  onSuccess,
  packageId,
  initialPackage,
  isDuplicating = false,
  duplicateSourcePackageId
}) => {
  const { t } = useTranslation(NAMESPACE.COMMON);
  const [saveStep, setSaveStep] = useState<'calculator' | 'name'>('calculator');
  const [form] = Form.useForm<{ name: string; description?: string; format?: string }>();

  const { data: tradingDealsData } = useGetTradingDealsQuery();
  // Memoised so the empty fallback keeps one identity for the hooks that depend on it.
  const tradingDeals = useMemo(() => tradingDealsData?.tradingDeals ?? [], [tradingDealsData]);

  const { data: sourcePackageDetail, isLoading: isLoadingDuplicateSource } =
    useGetCalculatorPackageByIdQuery(duplicateSourcePackageId!, {
      skip: !duplicateSourcePackageId
    });

  const packageTemplate = useMemo(() => {
    if (duplicateSourcePackageId && sourcePackageDetail) {
      return buildDuplicateInitialPackage(sourcePackageDetail);
    }
    return initialPackage;
  }, [duplicateSourcePackageId, sourcePackageDetail, initialPackage]);

  const calculatorReady = !duplicateSourcePackageId || Boolean(sourcePackageDetail);
  const isLoadingCalculator = Boolean(duplicateSourcePackageId) && !calculatorReady;

  const calculatorOptions = useMemo(() => {
    const initialRateCards = packageTemplate?.rows?.length
      ? packageTemplate.rows.map(packageRowToRateCardInput)
      : undefined;
    if (!packageTemplate) {
      return { initialRateCards };
    }
    const resetSeed = duplicateSourcePackageId
      ? `dup-${duplicateSourcePackageId}-${calculatorReady ? 'ready' : 'loading'}`
      : packageId
        ? `edit-${packageId}`
        : 'create';

    return {
      initialRateCards,
      initialSettings: calculatorSettingsFromPackage(packageTemplate, tradingDeals),
      initialFxRates: fxRatesFromPackage(packageTemplate),
      resetSeed
    };
  }, [packageTemplate, tradingDeals, duplicateSourcePackageId, calculatorReady, packageId]);

  const {
    settings,
    rateCards,
    kpiMetrics,
    isModalOpen,
    fxRates,
    updateSettings,
    updateFXRates,
    addRateCard: rawAddRateCard,
    deleteRateCard,
    updateRateCard,
    openModal,
    closeModal
  } = useEU6Calculator(calculatorOptions);

  const calculatorSnapshotRef = useRef({ settings, fxRates, rateCards });
  useEffect(() => {
    calculatorSnapshotRef.current = { settings, fxRates, rateCards };
  }, [settings, fxRates, rateCards]);

  const tradingDealSyncKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) {
      tradingDealSyncKeyRef.current = null;
      return;
    }
    if (!packageTemplate || tradingDeals.length === 0) return;
    const syncKey = `${duplicateSourcePackageId ?? packageId ?? 'new'}-${sourcePackageDetail?._id ?? packageTemplate.name}`;
    if (tradingDealSyncKeyRef.current === syncKey) return;
    tradingDealSyncKeyRef.current = syncKey;
    const next = tradingDealSettingsFromPackage(packageTemplate, tradingDeals);
    (Object.keys(next) as (keyof CalculatorSettingsState)[]).forEach((key) => {
      const value = next[key];
      if (value !== undefined) updateSettings(key, value as never);
    });
  }, [
    open,
    packageTemplate,
    tradingDeals,
    duplicateSourcePackageId,
    packageId,
    sourcePackageDetail?._id,
    updateSettings
  ]);

  const packageChannel =
    packageTemplate?.channel ?? (rateCards.length > 0 ? rateCards[0].channel : null);

  const [editingRateCard, setEditingRateCard] = useState<RateCard | null>(null);

  useEffect(() => {
    if (!isModalOpen) setEditingRateCard(null);
  }, [isModalOpen]);

  // Prefill package name / description (duplicate, edit-in-modal, or blank for new build).
  useEffect(() => {
    if (!open) return;
    form.setFieldsValue({
      name: packageTemplate?.name ?? '',
      description: packageTemplate?.description ?? ''
    });
  }, [open, packageTemplate, form]);

  const totalDeliveryPercentage = rateCards.reduce((sum, r) => sum + (Number(r.deliveryPercentage) || 0), 0);
  const otherDeliveryPercentageTotal = totalDeliveryPercentage - (editingRateCard?.deliveryPercentage ?? 0);
  const maxDeliveryPercentage = Math.max(0, 100 - otherDeliveryPercentageTotal);

  const addRateCard = useCallback(
    (input: RateCardInput) => {
      if (packageChannel != null && input.channel !== packageChannel) {
        message.error(t('singleChannelError', { channel: packageChannel }));
        return;
      }
      rawAddRateCard(input);
    },
    [packageChannel, rawAddRateCard, t]
  );

  const duplicateRateCard = useCallback(
    (card: RateCard) => {
      addRateCard({
        country: card.country,
        channel: card.channel,
        publisher: card.publisher,
        format: card.format,
        deliveryPercentage: card.deliveryPercentage,
        rowCurrency: card.rowCurrency,
        buyCPM: card.buyCPM,
        dspFeeId: card.dspFeeId,
        dspFeeName: card.dspFeeName,
        dspFeePercentage: card.dspFeePercentage
      });
    },
    [addRateCard]
  );

  const [createPackage, { isLoading: isCreating }] = useCreateCalculatorPackageMutation();
  const [updatePackage, { isLoading: isUpdating }] = useUpdateCalculatorPackageMutation();
  const isSaving = isCreating || isUpdating;

  const handleSaveAsPackage = useCallback(async () => {
    if (isLoadingCalculator) return;
    if (rateCards.length === 0) {
      message.error(t('noRows'));
      return;
    }
    const channels = [...new Set(rateCards.map((r) => r.channel))];
    if (channels.length > 1) {
      message.error(t('singleChannelError', { channel: packageChannel ?? channels[0] }));
      return;
    }
    if (isTotalDeliveryPercentageOver100(rateCards)) {
      message.warning(t('totalDeliveryExceeds100'));
      return;
    }
    try {
      await form.validateFields(['name']);
    } catch {
      return;
    }
    const packageFormat =
      [...new Set(rateCards.map((r) => r.format).filter(Boolean))].join(', ') || undefined;
    calculatorSnapshotRef.current = { settings, fxRates, rateCards };
    form.setFieldsValue({ format: packageFormat });
    setSaveStep('name');
  }, [rateCards, form, t, packageChannel, settings, fxRates, isLoadingCalculator]);

  const handleSubmitName = useCallback(async () => {
    try {
      const values = await form.validateFields();
      const saveSettings = settings;
      const saveFxRates = fxRates;
      const saveRateCards = rateCards;

      if (isTotalDeliveryPercentageOver100(saveRateCards)) {
        message.warning(t('totalDeliveryExceeds100'));
        return;
      }
      const name = values.name as string;
      const description = values.description as string | undefined;
      const rows = saveRateCards.map(rateCardToPackageRow);
      const channel = saveRateCards[0].channel;
      const format =
        [...new Set(saveRateCards.map((r) => r.format).filter(Boolean))].join(', ') || undefined;

      const totalImpressions = saveRateCards.reduce(
        (sum, r) => sum + (Number.isFinite(r.impressions) ? r.impressions : 0),
        0
      );
      const totalMediaSpendPlan = saveRateCards.reduce(
        (sum, r) => sum + (Number.isFinite(r.totalMediaSpendPlan) ? r.totalMediaSpendPlan : 0),
        0
      );
      const blendedBuyCpm = totalImpressions > 0 ? (totalMediaSpendPlan / totalImpressions) * 1000 : 0;

      const totalGrossBudgetPlan = saveRateCards.reduce((sum, card) => sum + card.grossBudgetPlan, 0);
      const totalNetBudgetPlan = saveRateCards.reduce((sum, card) => sum + card.netBudgetPlan, 0);
      const totalGrossMarginPlan = saveRateCards.reduce((sum, card) => sum + card.grossMarginPlan, 0);
      const totalGrossProfitPlan = saveRateCards.reduce((sum, card) => sum + card.grossProfitPlan, 0);
      const blendedMarginPercent =
        totalNetBudgetPlan > 0 ? (totalGrossMarginPlan / totalNetBudgetPlan) * 100 : 0;
      const blendedProfit = totalGrossProfitPlan;

      const writeBody = buildCalculatorPackageWriteBody({
        name,
        description,
        channel,
        format,
        rows,
        settings: saveSettings,
        fxRates: saveFxRates,
        tradingDeals,
        blendedMarginPercent,
        blendedProfit,
        blendedBuyCpm
      });

      let savedPackage: CalculatorPackage | undefined;
      if (packageId) {
        savedPackage = await updatePackage({
          packageId,
          body: writeBody as unknown as CreateCalculatorPackageBody
        }).unwrap();
      } else {
        savedPackage = await createPackage(writeBody as unknown as CreateCalculatorPackageBody).unwrap();
      }
      message.success(t('packageSaveSuccess'));
      const sentBudget = Number(saveSettings.totalBudget) || 0;
      if (packageBudgetPersistedMismatch(sentBudget, savedPackage)) {
        message.warning(
          `Package was created, but the server did not store total budget ${sentBudget.toLocaleString()}. ` +
            'Row gross budgets will look wrong until the backend persists totalBudget on POST/GET. ' +
            'See BACKEND_CALCULATOR_PACKAGES.md.'
        );
      }
      form.resetFields();
      setSaveStep('calculator');
      onSuccess(savedPackage);
      onClose();
    } catch (e) {
      // form or API error
    }
  }, [
    form,
    packageId,
    createPackage,
    updatePackage,
    t,
    onSuccess,
    onClose,
    settings,
    fxRates,
    rateCards,
    tradingDeals
  ]);

  const handleClose = useCallback(() => {
    if (saveStep === 'name') {
      setSaveStep('calculator');
      form.resetFields();
    }
    onClose();
  }, [saveStep, form, onClose]);

  if (!open) return null;

  if (saveStep === 'name') {
    return (
      <Modal
        title={packageId ? 'Update package' : isDuplicating ? t('duplicatePackage') : 'Save as package'}
        open
        onCancel={handleClose}
        footer={[
          <Button key="cancel" onClick={handleClose}>
            {t('cancel')}
          </Button>,
          <Button key="submit" type="primary" loading={isSaving} onClick={handleSubmitName}>
            {t('save')}
          </Button>
        ]}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="name"
            label={t('packageName')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Input placeholder="Package name" />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={3} placeholder="Description" />
          </Form.Item>
          <Form.Item name="format" label="Package format">
            <Input placeholder="e.g. 15s, 30s, 40s" readOnly />
          </Form.Item>
        </Form>
      </Modal>
    );
  }

  return (
    <Modal
      className="calculator-package-modal"
      title={packageId ? 'Edit package' : isDuplicating ? t('duplicatePackage') : 'Build package'}
      open
      onCancel={handleClose}
      width="90%"
      style={{ top: 16, maxWidth: 1200 }}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          {t('cancel')}
        </Button>,
        <Button key="save" type="primary" onClick={handleSaveAsPackage} disabled={isLoadingCalculator}>
          {t('saveAsPackage')}
        </Button>
      ]}
    >
      <PageContainer style={{ maxHeight: '70vh', overflow: 'auto', position: 'relative', padding: 0 }}>
        {isLoadingCalculator && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'rgba(10, 10, 54, 0.75)'
            }}
          >
            <Loader fullscreen={false} size="large" />
          </div>
        )}
        {packageChannel && (
          <div style={{ marginBottom: 12, color: APP_SHELL_THEME.textMuted }}>
            {t('singleChannelError', { channel: packageChannel })}
          </div>
        )}
        <Form form={form} layout="vertical" style={{ marginBottom: 16 }}>
          <Form.Item
            name="name"
            label={t('packageName')}
            rules={[{ required: true, message: t('requiredFieldMessage') }]}
          >
            <Input placeholder={t('packageName')} allowClear />
          </Form.Item>
          <Form.Item name="description" label="Description (optional)">
            <Input.TextArea rows={2} placeholder="Description" allowClear />
          </Form.Item>
        </Form>
        <CalculatorSettings
          settings={settings}
          fxRates={fxRates}
          onUpdate={updateSettings}
          onUpdateFXRates={updateFXRates}
        />
        <KPISummary
          metrics={kpiMetrics}
          currency={settings.displayCurrency}
        />
        <div>
          <div style={{ position: 'relative', marginBottom: 16, minHeight: 40 }}>
            <SectionHeading style={{ margin: 0 }}>Media Plan Rates</SectionHeading>
            <div
              style={{
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)'
              }}
            >
              <Button type="primary" icon={<PlusOutlined />} onClick={openModal}>
                Add Rate to Plan
              </Button>
            </div>
          </div>
          {rateCards.length === 0 ? (
            <TableCard>
              <EmptyState
                onAddRate={() => {
                  setEditingRateCard(null);
                  openModal();
                }}
              />
            </TableCard>
          ) : (
            <RatesTable
              rateCards={rateCards}
              onDelete={deleteRateCard}
              onEdit={(rate) => {
                setEditingRateCard(rate);
                openModal();
              }}
              onDuplicate={duplicateRateCard}
              currency={settings.displayCurrency}
            />
          )}
        </div>
        <AddRateModal
          open={isModalOpen}
          onClose={closeModal}
          onAdd={addRateCard}
          initialRateCard={editingRateCard}
          onEdit={(input) => {
            if (!editingRateCard) return;
            if (packageChannel != null && input.channel !== packageChannel) {
              message.error(t('singleChannelError', { channel: packageChannel }));
              return;
            }
            updateRateCard(editingRateCard.id, {
              country: input.country,
              channel: input.channel,
              publisher: input.publisher,
              format: input.format,
              deliveryPercentage: input.deliveryPercentage,
              rowCurrency: input.rowCurrency,
              buyCPM: input.buyCPM,
              dspFeeId: null,
              dspFeeName: input.dspFeeName,
              dspFeePercentage: input.dspFeePercentage
            });
            setEditingRateCard(null);
            closeModal();
          }}
          defaultCurrency={settings.displayCurrency}
          otherDeliveryPercentageTotal={otherDeliveryPercentageTotal}
          maxDeliveryPercentage={maxDeliveryPercentage}
        />
      </PageContainer>
    </Modal>
  );
};
